<script setup lang="ts">
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Alert, AlertDescription } from '~/components/ui/alert'
import { AlertCircle } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { useApiPost } from '~/composables/useApiPost'
import { WidgetPhoneInput } from '~/components/widget/phone-input'
import type { FormServerErrorResponse } from '~/types'
import { ANCHOR_IDS } from '~/lib/constants'
import { ContentBlock } from '~/components/ui/content-block'
import type { THeading } from '~/components/ui/heading'
import type { TContentBlockVariant } from '~/components/ui/content-block'

const config = useRuntimeConfig()
const { trackEvent } = useAnalytics()
const { t } = useI18n()

const props = defineProps<{
  headingText?: string
  headingClass?: string
  headingTag?: THeading
  variant?: TContentBlockVariant
}>()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredFieldLastName: useValidationMessage('requiredFieldLastName'),
  minValue2: useValidationMessage('minValue', 2),
  maxValue50: useValidationMessage('maxValue', 50),
  requiredFieldFirstName: useValidationMessage('requiredFieldFirstName'),
  requiredFieldMessage: useValidationMessage('requiredFieldMessage'),
  minValue1: useValidationMessage('minValue', 1),
  maxValue500: useValidationMessage('maxValue', 500),
  requiredFieldEmail: useValidationMessage('requiredFieldEmail'),
  requiredEmailInvalid: useValidationMessage('requiredEmailInvalid'),
  agreement: useValidationMessage('agreement'),

}

// Built in setup: the phone schema resolves its messages through useI18n
const phoneSchema = usePhoneValidationSchema()

const formSchema = computed(() =>
  toTypedSchema(
    z.object({
      lastname: z
        .string({ message: validationMessages.requiredFieldLastName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      firstname: z
        .string({ message: validationMessages.requiredFieldFirstName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      message: z
        .string({ message: validationMessages.requiredFieldMessage })
        .min(1, { message: validationMessages.minValue1 })
        .max(500, { message: validationMessages.maxValue500 }),
      phone: phoneSchema,
      email: z
        .string({ message: validationMessages.requiredFieldEmail })
        .email({ message: validationMessages.requiredEmailInvalid }),
      agreement: z
        .boolean({
          required_error: validationMessages.agreement
        })
        .refine((val) => val, {
          message: validationMessages.agreement
        })
    })
  )
)

const form = useForm({
  validationSchema: formSchema
})

const alert = ref(false)
const alertMessage = ref('')

const handleServerValidationErrors = <T extends string>(
  error: unknown,
  setFieldError: (field: T, message: string | string[] | undefined) => void,
  knownFields: T[]
) => {
  if (typeof error === 'object' && error !== null && '_data' in error) {
    const serverError = error as FormServerErrorResponse
    const errors = serverError._data?.errors

    if (errors && typeof errors === 'object') {
      for (const [key, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length) {
          if (key === 'fullname') {
            setFieldError('lastname' as T, messages[0])
            setFieldError('firstname' as T, messages[0])
          } else if (knownFields.includes(key as T)) {
            setFieldError(key as T, messages[0])
          }
        }
      }
    }
  }

}

const onSubmit = form.handleSubmit(async (values) => {
  const requestBody: Record<string, unknown> = {
    fullname: `${values.lastname} ${values.firstname}`,
    phone: `+996${values.phone}`,
    email: values.email,
    message: values.message
  }

  const { data, error } = await useApiPost<{ message: string }>('/contacts', requestBody)
  if (error) {
    handleServerValidationErrors(error, form.setFieldError, [
      'lastname',
      'firstname',
      'email',
      'phone',
      'message'
    ])
  } else {
    trackEvent('contact_form_submit', { form_type: 'contact' })
    alertMessage.value = data?.message ?? ''
    alert.value = true
  }
})
</script>

<template>
  <ContentBlock
    :id="ANCHOR_IDS.FEATURE_CONTACT_PAGE_FORM"
    :heading-text="props.headingText || $t('feature.contact_page_form.heading')"
    :heading-tag="props.headingTag"
    :heading-class="props.headingClass"
    :variant="props.variant"
    :show-link-icon="true"
    class="FormScrollOffset"
  >
    <slot name="description" />
    <Alert v-if="alert" variant="green">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>
        <div>{{ alertMessage }}</div>
      </AlertDescription>
    </Alert>
    <form
      v-else
      v-webmcp-tool="{
        name: 'contact_editorial_team',
        description:
          'Send a message to the Banks.kg editorial team: a question, feedback, advertising or partnership request.'
      }"
      class="flex flex-col gap-5"
      @submit="onSubmit"
    >
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField v-slot="{ componentField, errors }" name="lastname">
          <FormItem>
            <FormLabel>{{ t('common.last_name') }}*</FormLabel>
            <FormControl>
              <Input type="text" :placeholder="t('common.last_name')" v-bind="componentField" />
            </FormControl>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField, errors }" name="firstname">
          <FormItem>
            <FormLabel>{{ t('common.first_name') }}*</FormLabel>
            <FormControl>
              <Input v-bind="componentField" :placeholder="t('common.first_name')" />
            </FormControl>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField v-slot="{ componentField, errors }" name="phone">
          <FormItem class="md:flex-1">
            <FormLabel>{{ t('common.phone') }}*</FormLabel>
            <FormControl>
              <WidgetPhoneInput v-bind="componentField" :errors="errors.length" />
            </FormControl>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField, errors }" name="email">
          <FormItem class="md:flex-1">
            <FormLabel>Email*</FormLabel>
            <FormControl>
              <Input type="email" v-bind="componentField" placeholder="Email" />
            </FormControl>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>
      </div>
      <FormField v-slot="{ componentField, errors }" name="message">
        <FormItem>
          <FormLabel>{{ t('form.contact.message') }}*</FormLabel>
          <FormControl>
            <Textarea
              :placeholder="t('form.contact.message_placeholder')"
              v-bind="componentField"
            />
          </FormControl>
          <FormMessage v-if="errors.length" :message="errors[0]" />
        </FormItem>
      </FormField>
      <FormField v-slot="{ value, errorMessage, handleChange }" type="checkbox" name="agreement">
        <FormItem>
          <FormControl>
            <div class="flex items-center space-x-2">
              <Checkbox id="terms" :model-value="value" @update:model-value="handleChange" />
              <Label for="terms">
                {{ t('form.contact.agreement_prefix') }}
                <NuxtLinkLocale to="/privacy-policy" class="text-blue-600 underline">
                  {{ t('form.contact.agreement_link') }}
                </NuxtLinkLocale>
              </Label>
            </div>
          </FormControl>
          <FormMessage v-if="errorMessage" :message="errorMessage" />
        </FormItem>
      </FormField>

      <div class="flex flex-col items-start gap-4 md:flex-row md:justify-between">
        <Button type="submit" class="mt-4 md:mt-0">{{ t('form.contact.submit') }}</Button>
      </div>
    </form>
  </ContentBlock>
</template>
